import React from 'react';
import {classes, style} from './doppe-action-type-blog-post-widget.st.css';
import {ButtonType} from '@wix/devzai-utils-react';
import {
    doppeHideableValueCreateHidden,
    doppeHideableValueIsVisible,
    doppeHideableValueModifyValue,
    doppeHideableValueToggleHidden
} from '../../../client-server-common/types/doppe-hideable-value';
import {
    DoppeActionTypeWidgetHeader
} from '../../../components/doppe-action-type-widget-header/doppe-action-type-widget-header';
import {
    DoppeActionTypeWidgetInnerButton
} from '../../../components/doppe-action-type-widget-inner-button/doppe-action-type-widget-inner-button';
import {DoppeActionTypeBlogPost} from '../doppe-action-type-blog-post';
import {DoppeActionType} from '../../../client-server-common/doppe-action-types/doppe-action-type';
import {
    DoppeActionTypeWidgetAutoSizeImageView
} from '../../../components/doppe-action-type-widget-auto-size-image-view/doppe-action-type-widget-auto-size-image-view';
import {bymoPageContextPropsResolveBusinessEntity, useBymoPageContextProps} from '../../../doppe-sdk';
import {
    DoppeActionUnavailablePreview,
    DoppeActionUnavailableReason
} from '../../../components/doppe-action-unavailable-preview/doppe-action-unavailable-preview';
import {
    DoppeActionTypeWidgetInnerDescription
} from '../../../components/doppe-action-type-widget-inner-description/doppe-action-type-widget-inner-description';
import {
    DoppeActionTypeWidgetInnerTitle
} from '../../../components/doppe-action-type-widget-inner-title/doppe-action-type-widget-inner-title';
import {
    DoppeActionTypeWidgetContentLayoutNew
} from '../../../components/doppe-action-type-widget-content-layout-new/doppe-action-type-widget-content-layout-new';
import {
    DoppeActionTypeWidgetContentDate
} from '../../../components/doppe-action-type-widget-content-date/doppe-action-type-widget-content-date';
import {
    DoppeActionTypeWidgetContentUrl
} from '../../../components/doppe-action-type-widget-content-url/doppe-action-type-widget-content-url';
import {stringIsNotNullableOrWhiteSpace, stringIsNullableOrWhiteSpace} from '@wix/devzai-utils-common';
import {
    useDoppeViewerLangDictionary
} from '../../../components/doppe-viewer-lang-dictionary/doppe-viewer-lang-dictionary';
import {
    doppeBusinessEntityDtoIsNotFound,
    doppeExternalBlogPostProviderResolveBusinessEntityReference
} from '../../../client-server-common';
import {DoppeDtoBlogPost} from '../../../client-server-common/types/doppe-dto-blog-post';

export const DoppeActionTypeBlogPostWidget = React.memo(function DoppeActionTypeBlogPostWidget(
    props: DoppeActionType.WidgetProps<DoppeActionTypeBlogPost.ActionSettings>
) {
    const {
        actionContext,

        className,
        ...htmlAttributes
    } = props;

    return (
        <div
            {...htmlAttributes}
            className={style(classes.root, {}, className)}
        >
            <DoppeActionTypeWidgetHeader
                actionContext={actionContext}
                withBorderBottom={false}
            />
            <DoppeActionTypeBlogPostBindableWidget
                reportGoalAchieved={() => {
                    actionContext.reportGoalAchieved()
                }}
                actionId={actionContext.action.id}
                actionSettings={actionContext.action.settings}
            />
        </div>
    );
});

export const DoppeActionTypeBlogPostBindableWidget = React.memo(function DoppeActionTypeBlogPostBindableWidget(
    props: {
        actionSettings: DoppeActionTypeBlogPost.ActionSettings;
        reportGoalAchieved: () => void;
        actionId: string;
    }
) {
    const {
        actionSettings,
        reportGoalAchieved,
        actionId
    } = props;

    const bymoPageContextProps = useBymoPageContextProps();
    const {
        blogPostProvider,
        blogPostProviderId
    } = actionSettings;

    if (blogPostProvider !== null && blogPostProviderId !== null) {
        const boundBlogPost = bymoPageContextPropsResolveBusinessEntity(
            bymoPageContextProps,
            doppeExternalBlogPostProviderResolveBusinessEntityReference(blogPostProvider, blogPostProviderId))

        if (!boundBlogPost || doppeBusinessEntityDtoIsNotFound(boundBlogPost)) {
            return (
                <DoppeActionTypeBlogPostWidgetContentUnavailable
                    reason={
                        !boundBlogPost ?
                            DoppeActionUnavailableReason.ContentLoadingError :
                            DoppeActionUnavailableReason.ContentUnavailable
                    }
                />
            )
        } else {
            return (
                <DoppeActionTypeBlogPostWidgetInner
                    reportGoalAchieved={reportGoalAchieved}
                    actionId={actionId}
                    actionSettings={doppeActionTypeBlogPostWidgetResolveSettingsFromBoundEntity(actionSettings, boundBlogPost)}
                />
            )
        }
    } else {
        return (
            <DoppeActionTypeBlogPostWidgetInner
                reportGoalAchieved={reportGoalAchieved}
                actionId={actionId}
                actionSettings={actionSettings}
            />
        )
    }
});

export const DoppeActionTypeBlogPostWidgetContentUnavailable = React.memo(function DoppeActionTypeBlogPostWidgetContentUnavailable(
    props: {
        reason: DoppeActionUnavailableReason;
    }
) {
    const {
        reason
    } = props;

    const doppeViewerLang = useDoppeViewerLangDictionary();

    return (
        <DoppeActionUnavailablePreview
            reason={reason}
            title={doppeViewerLang.contentUnavailableTitleBlog}
        />
    )
});

export const DoppeActionTypeBlogPostWidgetInner = React.memo(function DoppeActionTypeBlogPostWidgetInner(
    props: {
        actionSettings: DoppeActionTypeBlogPost.ActionSettings;
        reportGoalAchieved: () => void;
        actionId: string;
    }
) {
    const {
        actionSettings,
        reportGoalAchieved,
    } = props;

    const {
        blogPostButtonText,
        blogPostLink,
        description,
        blogPostTitle,
        blogPostDate,
        blogPostImages,
        showBlogWebsite,
    } = actionSettings;

    const hideableBlogPostLink =
        doppeHideableValueToggleHidden(blogPostLink, stringIsNullableOrWhiteSpace(blogPostLink) || !showBlogWebsite)

    return (
        <DoppeActionTypeWidgetContentLayoutNew
            mainMedia={blogPostImages && blogPostImages.length > 0 ? (
                <DoppeActionTypeWidgetAutoSizeImageView
                    imageResource={blogPostImages[0]}
                />
            ) : null}
            footer={(
                <DoppeActionTypeWidgetInnerButton
                    buttonType={ButtonType.NormalButton}
                    tagName={'a'}
                    href={blogPostLink}
                    target={'_blank'}
                    onClick={() => {
                        reportGoalAchieved();
                    }}
                >
                    {blogPostButtonText}
                </DoppeActionTypeWidgetInnerButton>
            )}
        >

            <DoppeActionTypeWidgetInnerTitle>
                {blogPostTitle}
            </DoppeActionTypeWidgetInnerTitle>

            {doppeHideableValueIsVisible(blogPostDate) || doppeHideableValueIsVisible(hideableBlogPostLink) ? (
                <div>
                    <DoppeActionTypeWidgetContentDate>{blogPostDate}</DoppeActionTypeWidgetContentDate>
                    <DoppeActionTypeWidgetContentUrl>{hideableBlogPostLink}</DoppeActionTypeWidgetContentUrl>
                </div>
            ) : null}

            <DoppeActionTypeWidgetInnerDescription>
                {description}
            </DoppeActionTypeWidgetInnerDescription>

        </DoppeActionTypeWidgetContentLayoutNew>
    );
});

export function doppeActionTypeBlogPostWidgetResolveSettingsFromBoundEntity(
    actionSettings: DoppeActionTypeBlogPost.ActionSettings,
    doppeDtoBlogPost: DoppeDtoBlogPost
): DoppeActionTypeBlogPost.ActionSettings {
    return {
        ...actionSettings,
        blogPostDate: doppeDtoBlogPost.firstPublishedDate ?
            doppeHideableValueModifyValue(actionSettings.blogPostDate, doppeDtoBlogPost.firstPublishedDate) :
            doppeHideableValueCreateHidden(new Date(0)),
        showBlogWebsite: stringIsNotNullableOrWhiteSpace(doppeDtoBlogPost.blogPostPageUrl) ?
            actionSettings.showBlogWebsite : false,
        blogPostLink: stringIsNotNullableOrWhiteSpace(doppeDtoBlogPost.blogPostPageUrl) ?
            doppeDtoBlogPost.blogPostPageUrl : 'about:blank',
    }
}